// src/components/screens/ClubList.tsx
import React, { useState, useEffect } from 'react';
import { TextField, List, ListItem, ListItemText, ListItemIcon, Container, Typography, Box, Button, Grid, Link, Stepper, Step, StepLabel, StepIconProps, CircularProgress } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { footerLinks } from '../Shared/footerLinks';
import { theme, style, config, StyledRadio } from '../../../config/settings.jsx';
import { steps } from '../../../config/steps';
import { api } from '../../../config/apis.jsx';
import { getTheme } from '../../../config/theme';
import CheckIcon from '@mui/icons-material/Check';

const LaunchConsent = () => {
  const [subdomain, setSubdomain] = useState('');
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [theme, setTheme] = useState({});
  const [assetsPath, setAssetsPath] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [consentLink, setConsentLink] = useState('');
  const [cdrid, setCdrid] = useState('');
  const [error, setError] = useState('');
  
  // Get cdrid or ssoid from URL parameters
  const cdridFromUrl = searchParams.get('cdrid');
  const ssoidFromUrl = searchParams.get('ssoid');
  
  console.log("cdridFromUrl:", cdridFromUrl);
  console.log("ssoidFromUrl:", ssoidFromUrl);


  {/* Host name identification and configassets loading */}
useEffect(() => {
  setIsLoadingTheme(true);
  let subdomain;
  
  const loadConfigAndAssets = async () => {
  const hostname = window.location.hostname; 
  const parts = hostname.split('.'); 
  if (parts.length > 2 && parts[1] === 'sipora' && parts[2] === 'io') {
    subdomain = parts[0];
    setSubdomain(subdomain); 
  } else {
    subdomain = 'default';
  }
  localStorage.setItem('host', subdomain);

  const settings = await import(`../../../config/${subdomain}/settings.jsx`);
      setAssetsPath(settings.logoURL);  // Dynamic asset folder
  
  console.log(settings)
  setIsLoadingTheme(false);
  };

  loadConfigAndAssets();

}, []);

// Effect to retrieve cdrid from ssoid if needed
useEffect(() => {
  const fetchCdridFromSsoid = async () => {
    // If we have cdrid directly from URL, use it
    if (cdridFromUrl) {
      setCdrid(cdridFromUrl);
      localStorage.setItem('cdrid', cdridFromUrl);
      console.log("Using cdrid from URL:", cdridFromUrl);
      return;
    }

    // If we have ssoid, fetch the cdrid
    if (ssoidFromUrl) {
      console.log("Fetching cdrid for ssoid:", ssoidFromUrl);
      setIsLoading(true);
      try {
        const response = await axios.post(
          api.getUserSsoid,
          { ssoID: ssoidFromUrl },
          { 
            headers: { 
              'Content-Type': 'application/json',
              apikey: config.apikey,
              tenant: config.tenant
            } 
          }
        );
        
        console.log("API Response:", response.data);
        
        if (response.data?.userRecord?.document?._id) {
          const retrievedCdrid = response.data.userRecord.document._id;
          setCdrid(retrievedCdrid);
          localStorage.setItem('cdrid', retrievedCdrid);
          console.log("Retrieved cdrid from ssoid:", retrievedCdrid);
        } else {
          setError("User not found for the provided ssoid");
          console.error("No user found for ssoid:", ssoidFromUrl);
        }
      } catch (error) {
        console.error('Error retrieving user by ssoid:', error);
        setError("Failed to retrieve user information");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Check localStorage as fallback
    const storedCdrid = localStorage.getItem('cdrid');
    if (storedCdrid) {
      setCdrid(storedCdrid);
      console.log("Using cdrid from localStorage:", storedCdrid);
    }
  };

  fetchCdridFromSsoid();
}, [cdridFromUrl, ssoidFromUrl]);


  const fetchConsent = async () => {
    if (!cdrid) {
      setError("No user identifier found. Please provide cdrid or ssoid.");
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log("Fetching consent for cdrid:", cdrid);
      const response = await axios.post(
        api.consent,
        { cdrid: cdrid },
        { 
          headers: { 
            'Content-Type': 'application/json',
            apikey: config.apikey,
            tenant: config.tenant
          } 
        }
      );
      
      const consentLink = response.data.consentLink;
      console.log("Redirecting to consent link:", consentLink);
      window.location.href = consentLink;
    } catch (error) {
      console.error('Error creating consent link:', error);
      setError("Failed to create consent link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={getTheme}>
    <Container component="main" maxWidth="xs">
 
      <Box display="flex" flexDirection="column" alignItems="center" mt={4} p={2} >
      
  
      {isLoading && !cdrid ? (
        // Loading state while fetching cdrid from ssoid
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={8} style={{ width: '100%'}}>
          <CircularProgress />
          <Typography variant="body1" component="p" align="center" color="text.secondary" mt={2}>
            Loading user information...
          </Typography>
        </Box>
      ) : !cdrid && !ssoidFromUrl ? (
        // Error message when no cdrid or ssoid is provided
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={8} style={{ width: '100%'}}>
          <Typography variant="h5" component="h2" align="center" color="error" sx={{fontWeight: 'bold'}} gutterBottom>
            {error || "Error: Please provide cdrid or ssoid"}
          </Typography>
          <Typography variant="body1" component="p" align="center" color="text.secondary" mt={2}>
            A valid cdrid or ssoid parameter is required to proceed.
          </Typography>
        </Box>
      ) : error && !cdrid ? (
        // Error message when ssoid lookup failed
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={8} style={{ width: '100%'}}>
          <Typography variant="h5" component="h2" align="center" color="error" sx={{fontWeight: 'bold'}} gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" component="p" align="center" color="text.secondary" mt={2}>
            Please check your ssoid and try again.
          </Typography>
        </Box>
      ) : (
        <>
        <Grid container justifyContent="center" mb={2}>
           <Box display="flex" flexDirection="column" alignItems="center" mt={4} mb={6} style={{ width: '100%'}} >
        <Stepper activeStep={1} alternativeLabel>
        {steps.map((label) => (
          <Step key={label} >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
        </Box>
      
        <Typography variant="h4" component="h2" align="center" mb={2} sx={{fontWeight: 'bold'}} gutterBottom>
        Welcome
      </Typography>
      
      <Box display="flex" flexDirection="column" alignItems="left" mb={2} style={{ width: '100%'}}>
      <Typography variant="body" component="h2" align="left"  sx={{fontWeight: 'bold'}}>
        Let's set up your accounts:
      </Typography>
      <Typography variant="subtitle1" component="div" align="left" mb={2} gutterBottom>
 <List>
    <ListItem>
    <ListItemIcon>
      <CheckIcon style={{ color: '#47b3b5' }} /> {/* You can change the color */}
    </ListItemIcon>
    <ListItemText primary="Transactions accounts to track round-ups" />
      </ListItem>
    <ListItem>
    <ListItemIcon>
      <CheckIcon style={{ color: '#47b3b5' }} /> {/* You can change the color */}
    </ListItemIcon>
    <ListItemText primary="Funding account for micro debits Monday, Wednesday and Friday" />
      </ListItem>
    
  </List>
   </Typography>
  <Typography variant="body" component="h2" align="left"  sx={{fontWeight: 'bold'}}>
       To continue, you will need:
      </Typography>
      <Typography variant="subtitle1" component="div" align="left" mb={2} gutterBottom>
      <List>
  <ListItem>
    <ListItemIcon>
      <CheckIcon style={{ color: '#47b3b5' }} /> {/* You can change the color */}
    </ListItemIcon>
    <ListItemText primary="Internet Banking CRN - Customer Reference Number" />
  </ListItem>
  <ListItem>
    <ListItemIcon>
      <CheckIcon style={{ color: '#47b3b5' }} />
    </ListItemIcon>
    <ListItemText primary="Internet Banking App - they'll send you a code" />
  </ListItem>
</List></Typography>
   
      </Box>
       
      </Grid>
      
   
          <Button
            onClick={fetchConsent}
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, borderRadius: 3, width: "100%"  }}
            disabled={isLoading}

                  
          >
            {isLoading ? 'Loading...' : 'Continue'}
         
          </Button>


          { footerLinks }
        </>
      )}
      </Box>
    </Container> </ThemeProvider>
  );
};

export default LaunchConsent;

export {};